import { Badge, BlockStack, Box, Button, Image, InlineStack, Text, useBreakpoints } from '@shopify/polaris';
import ConditionalWrapper from 'components/shared/ConditionalWrapper';
import IfCondition from 'components/shared/If-condition/IfCondition';
import ResizeableSekeletonText from 'components/shared/skeletons/resizeable-skeleton-text/ResizeableSekeletonText';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import { AppRecurringPricingDetails } from 'hooks/shopify/queries/get-all-app-subscriptions.graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { transformToCdnUrl } from 'utils/google-storage-util';

const CurrentPlan = () => {
    const { t } = useTranslation();

    const { currentSubscription, isLoading, isLegacy, isCancelled, goToPLans, goToBilling, appHandle } =
        useCurrentSubscriptionHelper();

    const { mdUp } = useBreakpoints({ defaults: { mdUp: true } });

    const pricingDetails = useMemo(
        () => currentSubscription?.lineItems?.[0]?.plan?.pricingDetails as AppRecurringPricingDetails | undefined,
        [currentSubscription]
    );

    const planName = useMemo(() => {
        if (!currentSubscription) return t('DASHBOARD.CURRENT_PLAN.SUBTITLE_NOT_SUBBED');
        return isLegacy
            ? `${currentSubscription.name} (${t('DASHBOARD.CURRENT_PLAN.LEGACY')})`
            : currentSubscription.name;
    }, [currentSubscription, isLegacy, t]);

    const planCycle = useMemo(() => {
        if (!pricingDetails?.interval) return '';
        switch (pricingDetails.interval) {
            case 'ANNUAL':
                return t('DASHBOARD.CURRENT_PLAN.YEARLY');
            case 'EVERY_30_DAYS':
                return t('DASHBOARD.CURRENT_PLAN.MONTHLY');
            default:
                return '';
        }
    }, [pricingDetails, t]);

    const remainingTrialDays = useMemo(() => {
        if (!currentSubscription?.trialDays || !currentSubscription?.createdAt) return 0;
        const daysSinceCreation = Math.floor(
            (Date.now() - new Date(currentSubscription.createdAt).getTime()) / (1000 * 60 * 60 * 24)
        );
        return Math.max(0, currentSubscription.trialDays - daysSinceCreation);
    }, [currentSubscription]);

    return (
        <Box paddingBlock={'200'}>
            <ConditionalWrapper
                condition={mdUp}
                wrapper={(c) => (
                    <InlineStack blockAlign="center" align="space-between">
                        {c}
                    </InlineStack>
                )}
                falsyWrapper={(c) => <BlockStack gap={'400'}>{c}</BlockStack>}
            >
                <InlineStack gap={'400'} blockAlign="center">
                    <Image
                        source={transformToCdnUrl('embedded-assets/dashboard-images/current_plan.webp')}
                        alt="plan icon"
                        width="50px"
                        height="50px"
                    />
                    {/* <Icon source={PlanIcon} tone="base" /> */}
                    <BlockStack gap={'400'}>
                        <BlockStack align="center" gap={'100'}>
                            <IfCondition condition={isLoading}>
                                <BlockStack gap={'100'}>
                                    <ResizeableSekeletonText width="200px" height="20px" color="bg-fill-tertiary" />
                                    <ResizeableSekeletonText width="200px" height="20px" color="bg-fill-tertiary" />
                                </BlockStack>
                            </IfCondition>
                            <IfCondition condition={!isLoading}>
                                <Text as="p" variant="headingSm">
                                    {currentSubscription
                                        ? t('DASHBOARD.CURRENT_PLAN.TITLE')
                                        : t('DASHBOARD.CURRENT_PLAN.TITLE_NOT_SUBBED')}
                                </Text>
                                <InlineStack blockAlign="start" align="start" gap={'050'}>
                                    <Text as="p" variant="bodyMd">
                                        {planName}
                                    </Text>
                                    {planCycle !== '' && (
                                        <Text as="p" variant="bodyMd" tone="subdued">
                                            &#40;{planCycle.toLowerCase()}&#41;
                                        </Text>
                                    )}
                                    {remainingTrialDays > 0 && (
                                        <Badge>{remainingTrialDays + ' ' + t('DASHBOARD.CURRENT_PLAN.SUBTITLE')}</Badge>
                                    )}
                                    {isCancelled && (
                                        <Badge tone="critical">{t('DASHBOARD.CURRENT_PLAN.CANCELLED')}</Badge>
                                    )}
                                </InlineStack>
                            </IfCondition>
                        </BlockStack>
                    </BlockStack>
                </InlineStack>

                <BlockStack inlineAlign="end">
                    <InlineStack gap="200">
                        {currentSubscription && (
                            <Button onClick={goToBilling} loading={!appHandle || isLoading}>
                                {t('DASHBOARD.CURRENT_PLAN.VIEW_BILLING')}
                            </Button>
                        )}
                        <Button onClick={goToPLans} loading={isLoading}>
                            {currentSubscription ? t('DASHBOARD.CHANGE_PLAN') : t('DASHBOARD.START_FREE_TRIAL')}
                        </Button>
                    </InlineStack>
                </BlockStack>
            </ConditionalWrapper>
        </Box>
    );
};

export default CurrentPlan;
